import { Box, Grid } from "@mui/material";
import dayjs from "dayjs";
import { useMemo } from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import Flex from "../../../../../../components/Flex";
import { NoDataAvailable } from "../../../../../../components/NoDataAvailable";
import Text from "../../../../../../components/Text";
import CustomTitle from "../../../../../../components/app/CustomTitle";
import {
  ACCOUNT_RECEIVALES_COLORS,
  CHART_COLORS,
  CHART_TYPOGRAPHY,
} from "../../../../../../components/app/provider/patient/chart/tokens";
import { useAccountReceivableSummary } from "../../../../../../queries/account-receivables/use-account-receivable-summary";
import { getXtenantId } from "../../../../../../utils/utils";

type AccountReceivableSummaryRow = {
  duration?: string;
  patient?: number;
  insurance?: number;
  total?: number;
};

type ChartRow = {
  duration: string;
  patient: number;
  insurance: number;
  total: number;
};

type StatCardProps = {
  title: string;
  value: string | number;
};

const StatCard = ({ title, value }: StatCardProps) => (
  <Box
    sx={{
      backgroundColor: ACCOUNT_RECEIVALES_COLORS.duePaymentCardsBg,
      border: `1px solid ${CHART_COLORS.textMutedGrey}`,
      borderRadius: 1,
      p: 2,
    }}
  >
    <Text c={CHART_COLORS.textMutedGrey} mb={0.5}>
      {title}
    </Text>
    <Text c={CHART_COLORS.textDarker} fontWeight={600} size={"18px"}>
      {value}
    </Text>
  </Box>
);

const kFormatter = (num: any) => {
  if (num >= 1_000_000) return (num / 1_000_000).toFixed(1) + "M";
  if (num >= 1_000) return (num / 1_000).toFixed(1) + "K";
  return num;
};

const AccountReceivableSummery = () => {
  /**
   * The Summary tab has no Date / Group By filters — it always reports on the
   * current day, from the start of today to the end of today.
   */
  const { startDate, endDate } = useMemo(
    () => ({
      startDate: dayjs().startOf("day").toISOString(),
      endDate: dayjs().endOf("day").toISOString(),
    }),
    []
  );

  const { data: accountReceivableSummary } = useAccountReceivableSummary({
    startDate,
    endDate,
    xTENANTID: getXtenantId(),
  });

  const statCardData = (accountReceivableSummary as any)?.data?.[
    (accountReceivableSummary as any)?.data?.length - 1
  ];

  const DUE_PAYMENTS: StatCardProps[] = [
    { title: "Insurance Payment Dues", value: statCardData?.insurance },
    { title: "Patient Payment Dues", value: statCardData?.patient },
    { title: "Total Due", value: statCardData?.total },
  ];

  const chartData = useMemo<ChartRow[]>(() => {
    const rows =
      (accountReceivableSummary as { data?: AccountReceivableSummaryRow[] })
        ?.data ?? [];
    return rows.map((row) => ({
      duration: row?.duration ?? "",
      patient: row?.patient ?? 0,
      insurance: row?.insurance ?? 0,
      total: row?.total ?? 0,
    }));
  }, [accountReceivableSummary]);

  const axisTickStyle = {
    fontSize: 12,
    fill: CHART_COLORS.textMutedGrey,
    fontFamily: CHART_TYPOGRAPHY.fontFamily,
  };

  const axisLabelStyle = {
    fontSize: 12,
    fill: CHART_COLORS.textMutedGrey,
    fontFamily: CHART_TYPOGRAPHY.fontFamily,
    fontWeight: 400,
  };

  return (
    <Flex flexDirection={"column"} width="100%" gap={3}>
      <Box>
        <CustomTitle>Due Payments</CustomTitle>
        <Grid container spacing={2} mt={0}>
          {DUE_PAYMENTS.map((item) => (
            <Grid item xs={12} sm={4} key={item.title}>
              <StatCard title={item.title} value={item.value ?? 0} />
            </Grid>
          ))}
        </Grid>
      </Box>

      <Box
        sx={{
          backgroundColor: CHART_COLORS.surfaceWhite,
          border: `1px solid ${CHART_COLORS.borderLight}`,
          borderRadius: 1,
          p: 2,
        }}
      >
        <Flex
          width="100%"
          alignItems={"center"}
          justifyContent={"space-between"}
          mb={2}
        ></Flex>

        <Box sx={{ width: "100%", height: 360 }}>
          {chartData.length > 0 ? (
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={chartData}
                margin={{ top: 16, right: 24, left: 8, bottom: 24 }}
                barGap={6}
                barCategoryGap={"20%"}
              >
                <CartesianGrid
                  strokeDasharray="3 3"
                  stroke={CHART_COLORS.borderLight}
                  vertical={false}
                />
                <XAxis
                  dataKey="duration"
                  tick={axisTickStyle}
                  tickLine={false}
                  axisLine={{ stroke: CHART_COLORS.borderLight }}
                  interval={0}
                  label={{
                    value: "Aging Period",
                    position: "insideBottom",
                    offset: -12,
                    style: axisLabelStyle,
                  }}
                />
                <YAxis
                  tick={axisTickStyle}
                  tickLine={false}
                  axisLine={{ stroke: CHART_COLORS.borderLight }}
                  tickCount={10}
                  tickFormatter={(value) => kFormatter(value) as any}
                  label={{
                    value: "Revenue Generated ($)",
                    angle: -90,
                    position: "insideLeft",
                    offset: 10,
                    style: { ...axisLabelStyle, textAnchor: "middle" },
                  }}
                  width={100}
                />
                <Tooltip
                  cursor={{ fill: CHART_COLORS.surfaceHeader }}
                  contentStyle={{
                    border: `1px solid ${CHART_COLORS.borderLight}`,
                    borderRadius: 4,
                    fontSize: 12,
                    fontFamily: CHART_TYPOGRAPHY.fontFamily,
                  }}
                />
                <Legend
                  verticalAlign="top"
                  align="right"
                  iconType="square"
                  iconSize={15}
                  wrapperStyle={{
                    paddingBottom: 12,
                    fontSize: 12,
                    color: CHART_COLORS.textMutedGrey,
                    fontFamily: CHART_TYPOGRAPHY.fontFamily,
                  }}
                />
                <Bar
                  dataKey="patient"
                  name="Patient"
                  fill={ACCOUNT_RECEIVALES_COLORS.patientBar}
                  radius={[4, 4, 0, 0]}
                  maxBarSize={31}
                />
                <Bar
                  dataKey="insurance"
                  name="Insurance"
                  fill={ACCOUNT_RECEIVALES_COLORS.insuranceBar}
                  radius={[4, 4, 0, 0]}
                  maxBarSize={31}
                />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <NoDataAvailable message="No data available" />
          )}
        </Box>
      </Box>
    </Flex>
  );
};

export default AccountReceivableSummery;
